import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CURRENCY, STATUS } from 'src/constants/common';

import { InitialState } from '../app/MyQuote/QuoteDetail/quote.interface';
import { addActiveQuoteTag, sortDataByIsQuoted } from '../app/MyQuote/quotingDashboard.helpers';

import { RootState } from './store';

const initialState: InitialState = {
    quote: {},
    dimensions: [],
    addressDetails: {},
    loadLinkPosts: [],
    services: [],
    isShowQuoteDetailModal: false,
    activeTab: 'address',
    isViewQuote: true,
    activeQuotes: [],
    commonCarrierRates: [],
    spotQuoteRates: [],
    currencyType: CURRENCY.CAD,
    ratesTableCurrency: CURRENCY.CAD,
    loadPostingTableCurrency: CURRENCY.CAD,
    isStatusWonOrLost: false,
    isOpenPostToLoadBoard: false,
    loadPostingDetail: {},
    isViewLoadPost: false,
    customerOverview: {
        avgWinLoss: 0,
        customerName: '',
        customerStrategy: null,
        customerTags: '',
        laneWinLoss: 0,
        negotiation: '',
        isStrategyView: false,
        customerStatus: null,
    },
    predictiveIndex: {},
    isRedirectedFromRateGatherAgent: false,
    quoteConnectedUsers: [],
    routeDistance: 0,
    isQuoteDetailsUpdated: false,
    isShowLinkThreadModal: false,
    showTranscriptPanel: false,
    selectedLeadData: null,
    commonCarriers: [],
    selectedCommonCarrierData: null,
    isShowCommonCarriersOverviewModal: false,
    isLoading: true,
    selectedRateLoadPosting: null,
    rateData: [],
    rateCount: 0,
    isRatesLoading: false,
    isSpotQuoteLoadingWhenEditQuoteDetail: false,
    activeQuoteColumns: null,
    activeQuotesFetchedForId: null,
    activities: [],
    activitiesPagination: null,
    activitiesLoading: false,
    activitiesPostLoading: false,
};

export const QuotingSlice = createSlice({
    name: 'quoting',
    initialState,
    reducers: {
        resetQuoteState: (state) => ({
            ...initialState,
            activeQuotes: state.activeQuotes,
            activeQuoteColumns: state.activeQuoteColumns,
            activeQuotesFetchedForId: state.activeQuotesFetchedForId,
            activities: state.activities,
            activitiesPagination: state.activitiesPagination,
            activitiesLoading: state.activitiesLoading,
            activitiesPostLoading: state.activitiesPostLoading,
        }),
        resetAllQuoteState: () => initialState,
        setQuote: (state, { payload }) => {
            state.quote = payload;
        },

        setDimensions: (state, { payload }) => {
            state.dimensions = payload;
        },

        setAddressDetails: (state, { payload }) => {
            state.addressDetails = {
                ...state.addressDetails,
                ...payload,
            };
        },

        setServices: (state, { payload }) => {
            state.services = payload;
        },

        setIsShowQuoteDetailModal: (state, { payload }) => {
            state.isShowQuoteDetailModal = payload;
        },

        setActiveTabQuote: (state, { payload }) => {
            state.activeTab = payload;
        },

        setIsViewQuote: (state, { payload }) => {
            state.isViewQuote = payload;
        },

        setActiveQuotes: (state, action: PayloadAction<any[]>) => {
            state.activeQuotes = action.payload;
            sessionStorage.setItem(`activeCarrierCount_${state.addressDetails.id}`, state.activeQuotes.length.toString());
        },

        updateRemoveActiveQuote: (
            state,
            action: PayloadAction<{
                activeQuotes: any[];
                isRemove?: boolean;
            }>
        ) => {
            const { activeQuotes, isRemove } = action.payload;

            const activeQuoteIds = activeQuotes.map((quote) => quote.id);

            if (isRemove) {
                state.activeQuotes = state.activeQuotes.filter((quote) => !activeQuoteIds.includes(quote.id));

                if (state.activeQuotes.length === 0) {
                    state.addressDetails.status = STATUS.OPENED;
                } else {
                    state.activeQuotes = sortDataByIsQuoted(addActiveQuoteTag(state.activeQuotes));
                }

                activeQuoteIds.forEach((id) => {
                    if (state.addressDetails?.toBookQuoteCarrierId == id) {
                        state.addressDetails.toBookQuoteCarrierId = null;

                        state.addressDetails.status = state.activeQuotes.length > 0 ? STATUS.ACTIVE : STATUS.OPENED;
                    }
                });
            } else {
                // UPDATE ACTIVE QUOTES
                const updatedActiveQuotes = state.activeQuotes.map((quote) => {
                    if (activeQuoteIds.includes(quote.id)) {
                        return activeQuotes.find((q) => q.id === quote.id);
                    }

                    return quote;
                });

                // ADD ACTIVE QUOTES
                const newActiveQuotes = activeQuotes.filter((quote) => !state.activeQuotes.some((q) => q.id === quote.id));

                state.activeQuotes = sortDataByIsQuoted(addActiveQuoteTag([...updatedActiveQuotes, ...newActiveQuotes]));
            }

            sessionStorage.setItem(`activeCarrierCount_${state.addressDetails.id}`, state.activeQuotes.length.toString());
        },

        updateActiveQuoteValue: (state, { payload }) => {
            state.activeQuotes = state.activeQuotes.map((quote) => {
                if (quote.id === payload.id) {
                    return { ...quote, ...payload };
                }

                return quote;
            });
        },

        setQuoteConnectedUsers: (state, { payload }) => {
            state.quoteConnectedUsers = payload;
        },

        setPredictiveIndex: (state, { payload }) => {
            state.predictiveIndex = payload;
        },

        setIsQuoteDetailsUpdated: (state, { payload }) => {
            state.isQuoteDetailsUpdated = payload;
        },

        setShowTranscriptPanel: (state, { payload }) => {
            state.showTranscriptPanel = payload;
        },

        setSelectedLeadData: (state, { payload }) => {
            state.selectedLeadData = payload;
        },

        setIsLoading: (state, { payload }) => {
            state.isLoading = payload;
        },

        setActiveQuoteColumns: (state, action: PayloadAction<Record<string, boolean> | null>) => {
            state.activeQuoteColumns = action.payload;
        },

        setActiveQuotesFetchedForId: (state, action: PayloadAction<string | number | null>) => {
            state.activeQuotesFetchedForId = action.payload;
        },

        setActivities: (state, { payload }) => {
            state.activities = payload;
        },

        appendActivities: (state, { payload }) => {
            state.activities = [...state.activities, ...payload];
        },

        prependActivity: (state, { payload }) => {
            state.activities = [payload, ...state.activities];
        },

        setActivitiesPagination: (state, { payload }) => {
            state.activitiesPagination = payload;
        },

        setActivitiesLoading: (state, { payload }) => {
            state.activitiesLoading = payload;
        },

        setActivitiesPostLoading: (state, { payload }) => {
            state.activitiesPostLoading = payload;
        },
    },
});

export default QuotingSlice.reducer;

export const {
    resetQuoteState,
    resetAllQuoteState,
    setQuote,
    setDimensions,
    setAddressDetails,
    setServices,
    setIsShowQuoteDetailModal,
    setActiveTabQuote,
    setIsViewQuote,
    setActiveQuotes,
    updateRemoveActiveQuote,
    setQuoteConnectedUsers,
    setPredictiveIndex,
    setIsQuoteDetailsUpdated,
    setShowTranscriptPanel,
    setSelectedLeadData,
    updateActiveQuoteValue,
    setIsLoading,
    setActiveQuoteColumns,
    setActiveQuotesFetchedForId,
    setActivities,
    appendActivities,
    prependActivity,
    setActivitiesPagination,
    setActivitiesLoading,
    setActivitiesPostLoading,
} = QuotingSlice.actions;

export const quoteData = (state: RootState) => state.quoting.quote || {};

export const dimensionsData = (state: RootState) => state.quoting.dimensions || [];

export const addressDetailsData = (state: RootState) => state.quoting.addressDetails || {};

export const servicesData = (state: RootState) => state.quoting.services || [];

export const isShowQuoteDetailModalData = (state: RootState) => state.quoting.isShowQuoteDetailModal;

export const activeTabData = (state: RootState) => state.quoting.activeTab || 'address';

export const isViewQuoteData = (state: RootState) => state.quoting.isViewQuote;

export const activeQuotesData = (state: RootState) => state.quoting.activeQuotes || [];

export const isStatusWonOrLostData = (state: RootState) => state.quoting.isStatusWonOrLost || false;

export const isQuoteDetailsUpdatedData = (state: RootState) => state.quoting.isQuoteDetailsUpdated;

export const activeQuoteColumnsData = (state: RootState) => state.quoting.activeQuoteColumns ?? null;

export const activitiesData = (state: RootState) => state.quoting.activities || [];

export const activitiesPaginationData = (state: RootState) => state.quoting.activitiesPagination ?? null;

export const activitiesLoadingData = (state: RootState) => state.quoting.activitiesLoading;

export const activitiesPostLoadingData = (state: RootState) => state.quoting.activitiesPostLoading;
